import { useEffect, useState } from "react"
import { Experiment } from "@amplitude/experiment-js-client"
import type { ExperimentClient } from "@amplitude/experiment-js-client"
import { useViewerMaybe } from "~/auth/use-viewer"
import { getAnonymousId } from "~/common/anonymous-id"
import { getMetaContentMaybe } from "~/common/get-meta-content"

// Get Amplitude keys from meta tags
const AMPLITUDE_DEPLOYMENT_KEY = getMetaContentMaybe<string>("AMPLITUDE_DEPLOYMENT_KEY")

// Initialize Amplitude Experiment client
let experimentClient: ExperimentClient | null = null

const getExperimentClient = (): ExperimentClient | null => {
  const deploymentKey = AMPLITUDE_DEPLOYMENT_KEY

  if (!deploymentKey) {
    console.warn("Amplitude deployment key or API key not found for experiments")
    return null
  }

  if (!experimentClient) {
    // Initialize with Amplitude Analytics integration for proper exposure tracking
    experimentClient = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
      debug: process.env.NODE_ENV !== "production",
      fallbackVariant: {},
      initialVariants: {},
    })
  }

  return experimentClient
}

/**
 * Hook for Amplitude A/B testing using official Amplitude Experiment SDK
 * @param flagKey - The feature flag key in Amplitude
 * @param defaultVariant - Default variant to use initially and if experiment fails
 * @returns Object with variant and loading state
 */
export function useAmplitudeExperiment(flagKey: string, defaultVariant: string = "control") {
  const { viewer } = useViewerMaybe()
  const [variant, setVariant] = useState<string>(defaultVariant)
  const [isLoading, setIsLoading] = useState(false)

  // Initialize experiment and fetch variants
  useEffect(() => {
    const initializeExperiment = async () => {
      const client = getExperimentClient()
      if (!client) {
        return
      }

      setIsLoading(true)

      try {
        // Get user ID (authenticated or anonymous)
        const userId = viewer?.id || (await getAnonymousId())

        // Set user context for experiment
        const user = {
          user_id: userId,
          ...(viewer?.id && {
            user_type: "authenticated",
            user_role: viewer.userRole,
          }),
          ...(!viewer?.id && { user_type: "anonymous" }),
        }

        // Fetch variants from Amplitude
        await client.fetch(user)

        // Get the specific variant for this flag
        const variants = client.all()
        const flagVariant = variants[flagKey]

        if (flagVariant && flagVariant.value) {
          setVariant(flagVariant.value)

          // Track exposure immediately when we get the variant
          client.exposure(flagKey)
        }
      } catch (error) {
        console.error("Error fetching Amplitude experiment variants:", error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeExperiment()
  }, [flagKey, defaultVariant, viewer?.id, viewer?.userRole])

  return {
    variant,
    isLoading,
  }
}
